import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, KeyRound, UtensilsCrossed, BedDouble, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIAS = [
  { key: 'hoteleria', label: 'Hotelería', icon: BedDouble, tone: 'emerald' },
  { key: 'casino',    label: 'Casino',    icon: UtensilsCrossed, tone: 'amber' },
];

export default function SgReglas() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [reglas, setReglas] = useState({ hoteleria: [], casino: [] });
  const [tab, setTab] = useState('hoteleria');
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/servicios-generales/reglas`, { headers });
      setReglas({ hoteleria: r.data.hoteleria || [], casino: r.data.casino || [] });
      setDirty({});
    } catch (e) {
      toast.error('No se pudieron cargar las reglas');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onChange = (id, valor) => {
    setDirty(prev => ({ ...prev, [id]: valor }));
  };

  const guardar = async () => {
    const cambios = Object.entries(dirty).map(([id, valor]) => ({ id: Number(id), valor }));
    if (cambios.length === 0) { toast.info('No hay cambios que guardar'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/servicios-generales/reglas`, { reglas: cambios }, { headers });
      toast.success('Reglas guardadas.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const activas = reglas[tab] || [];
  const currentTab = CATEGORIAS.find(c => c.key === tab);
  const Icon = currentTab?.icon;

  return (
    <div className="space-y-4" data-testid="sg-reglas-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reglas</h1>
          <p className="text-sm text-slate-500">Configuración operacional de Casino y Hotelería.</p>
        </div>
        <button onClick={guardar} disabled={saving || Object.keys(dirty).length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
          data-testid="btn-guardar-reglas">
          <Save className="w-4 h-4" /> {saving ? 'Guardando…' : `Guardar (${Object.keys(dirty).length})`}
        </button>
      </div>

      {/* Tabs */}
      <div className="inline-flex bg-white border border-slate-200 rounded-lg p-0.5">
        {CATEGORIAS.map(c => (
          <button key={c.key} onClick={() => setTab(c.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md transition-colors ${tab === c.key ? 'bg-emerald-700 text-white shadow' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid={`tab-reglas-${c.key}`}>
            <c.icon className="w-4 h-4" /> {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400 py-6">Cargando…</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
            {Icon && <Icon className="w-5 h-5 text-emerald-700" />}
            <h3 className="text-base font-bold text-emerald-900">Reglas de {currentTab?.label}</h3>
          </div>
          {activas.length === 0 && <div className="text-sm text-slate-400 py-4">No hay reglas configurables en esta categoría.</div>}
          {activas.map(r => {
            const isPassword = r.clave === 'qr_password';
            const isEstadosQr = r.clave === 'estados_permitidos_qr';
            const val = dirty[r.id] !== undefined ? dirty[r.id] : (r.valor || '');
            return (
              <div key={r.id} className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-3 items-start py-2 border-b last:border-b-0 border-slate-50">
                <div>
                  <div className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                    {isPassword && <KeyRound className="w-3.5 h-3.5 text-amber-500" />}
                    {labelize(r.clave)}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{r.descripcion || '—'}</div>
                </div>
                <div>
                  {isPassword ? (
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={val}
                        onChange={e => onChange(r.id, e.target.value)}
                        className="w-full max-w-xs px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono tracking-widest"
                        data-testid={`regla-${r.clave}`}
                        placeholder="••••"
                      />
                      <button type="button" onClick={() => setShowPassword(p => !p)}
                        className="ml-2 p-2 text-slate-500 hover:text-slate-800 align-middle" title={showPassword ? 'Ocultar' : 'Mostrar'}>
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 max-w-md">
                        Esta clave es la que el personal de aseo deberá introducir al escanear el QR de una habitación para cambiar su estado.
                      </p>
                    </div>
                  ) : isEstadosQr ? (
                    <EstadosMultiSelect value={val} onChange={(v) => onChange(r.id, v)} testid={`regla-${r.clave}`} />
                  ) : r.clave.startsWith('horario_') ? (
                    <input
                      type="time"
                      value={val || ''}
                      onChange={e => onChange(r.id, e.target.value)}
                      className="w-40 px-3 py-2 text-sm border border-slate-300 rounded-lg font-mono"
                      data-testid={`regla-${r.clave}`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={val}
                      onChange={e => onChange(r.id, e.target.value)}
                      className="w-full max-w-md px-3 py-2 text-sm border border-slate-300 rounded-lg"
                      data-testid={`regla-${r.clave}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function labelize(clave) {
  return clave
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace('Qr', 'QR');
}

// Estados que se pueden habilitar como opciones en el QR físico
const ESTADO_OPCIONES = [
  { key: 'disponible',    label: 'Disponible',    dot: 'bg-emerald-500', card: 'bg-emerald-50 border-emerald-300 text-emerald-800' },
  { key: 'aseo',          label: 'En aseo',       dot: 'bg-amber-400',   card: 'bg-amber-50 border-amber-300 text-amber-800' },
  { key: 'mantenimiento', label: 'Mantenimiento', dot: 'bg-orange-500',  card: 'bg-orange-50 border-orange-300 text-orange-800' },
  { key: 'deshabilitada', label: 'Deshabilitada', dot: 'bg-slate-400',   card: 'bg-slate-100 border-slate-300 text-slate-700' },
];

function EstadosMultiSelect({ value, onChange, testid }) {
  const seleccionados = new Set(
    (value || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
  );
  const toggle = (k) => {
    const next = new Set(seleccionados);
    if (next.has(k)) next.delete(k); else next.add(k);
    // Mantener el orden canónico definido en ESTADO_OPCIONES
    const orden = ESTADO_OPCIONES.map(o => o.key).filter(o => next.has(o));
    onChange(orden.join(','));
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {ESTADO_OPCIONES.map(o => {
          const sel = seleccionados.has(o.key);
          return (
            <button key={o.key} type="button" onClick={() => toggle(o.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold uppercase tracking-wide transition-all ${sel ? o.card + ' ring-2 ring-emerald-500 ring-offset-1' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
              data-testid={`opt-estado-${o.key}`}>
              <span className={`w-2 h-2 rounded-full ${o.dot}`} />
              {o.label}
              {sel && <span className="text-emerald-700">✓</span>}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        Selecciona los estados que verán los operadores al escanear el QR. Se guardan en el orden mostrado.
      </p>
    </div>
  );
}
